import {
  BASIC_TYPE,
  InitAudioProcessorOptions,
  UpdateAudioProcessorOptions
} from 'trtc-js-sdk-core/src/enums';
import { IPlugin, Core } from 'trtc-js-sdk-core/src/plugin';
import { ValidateRule } from 'trtc-js-sdk-core/src/utils/decorators/validate';

import RtcError from '../common/rtc-error.js';
import { ErrorCode, ErrorCodeDictionary } from '../common/error-code.js';
import { getAudioContext } from 'trtc-js-sdk-core/src/utils/audio-context.js';
import loadWorklet from 'trtc-js-sdk-core/src/common/volume-meter/worklet-loader.js';
import { AudioReferenceChangeType, IRoom, NAME, formatDate } from 'trtc-js-sdk-core';
import { getScheduleDomain, scheduleProxy } from 'trtc-js-sdk-core/src/utils/schedule.js';
import innerEmitter from 'trtc-js-sdk-core/src/inner-emitter/inner-emitter.js';
import { INNER_EVENT } from 'trtc-js-sdk-core/src/inner-emitter/inner-type.js';
import { AudioNodeConnector, AudioSource } from 'trtc-js-sdk-core/src/manager/audio-mixer.js';
import { isOverseaSdkAppId, isUndefined } from 'trtc-js-sdk-core/src/utils/index.js';
import { getDeviceModel, getOSString, isMobile } from 'trtc-js-sdk-core/src/utils/environment.js';

export enum AUDIO_REFERENCE_TYPE {
  REMOTE_AUDIO = 'ra',
  AUDIO_MIXER_PLUGIN = 'ax',
  EAR_MONITOR = 'em',
  OTHER = 'o'
}

let adSeq = 0;
let preloadPromise: Promise<void>;

// *audio-processor 为 aec 插件
export class AudioProcessor implements IPlugin {
  log: Core['log'];
  customAudioReferenceMap: Map<AudioSource, string> = new Map();
  audioRefId = 0;
  audioContext: AudioContext = getAudioContext();
  localAudioWorkletNode?: AudioWorkletNode;
  screenAudioWorkletNode?: AudioWorkletNode;
  mixNode?: GainNode;
  silentNode?: ConstantSourceNode; // 自动补静音帧
  static getStartValidateRule(core: Core): ValidateRule {
    return {
      name: 'options',
      required: true,
      type: BASIC_TYPE.Object,
      properties: {
        sdkAppId: { type: BASIC_TYPE.Number, required: true },
        userId: { type: BASIC_TYPE.String, required: true },
        userSig: { type: BASIC_TYPE.String, required: true }
      },
      validate(params: any, key: string, fnName: string, className: string) {
        if (!core.room.audioManager.hasAudioTrack && !core.room.audioManager.hasScreenAudioTrack) {
          throw new RtcError({
            code: ErrorCode.INVALID_OPERATION,
            extraCode: ErrorCodeDictionary.INVALID_OPERATION_NEED_AUDIO,
            fnName
          });
        }
      }
    };
  }
  static updateValidateRule: ValidateRule = {
    type: BASIC_TYPE.Object
  };
  static stopValidateRule: ValidateRule = {
    type: BASIC_TYPE.Object
  };

  constructor(public core: Core) {
    adSeq = adSeq + 1;
    this.log = core.log.createChild({ id: `${this.getAlias()}${adSeq}` });
    this.log.info(`created id=${this.getAlias()}${adSeq}`);
    this.installEvent();
  }

  private preload(path: string) {
    if (!preloadPromise) {
      preloadPromise = this.doPreload(path);
    }
    return preloadPromise;
  }

  private async doPreload(path: string) {
    const blob = await this.core.fileDownloader.download(path, { type: 'blob' }) as Blob;
    const moduleUrl = URL.createObjectURL(blob);
    try {
      await loadWorklet(this.audioContext, moduleUrl);
    } catch (e) {
      this.log.error(`preload audioProcessor failed. ${e}`);
    } finally {
      URL.revokeObjectURL(moduleUrl);
    }
  }

  static Name = 'AudioProcessor';
  getName() {
    return AudioProcessor.Name;
  }

  getAlias(): string {
    return 'ap';
  }

  getGroup(): string {
    return 'ap';
  }

  getValidateRule(method: 'start' | 'update' | 'stop'): ValidateRule {
    switch (method) {
      case 'start':
        return AudioProcessor.getStartValidateRule(this.core);
      case 'update':
        return AudioProcessor.updateValidateRule;
      case 'stop':
        return AudioProcessor.stopValidateRule;
    }
  }

  async start(options: InitAudioProcessorOptions) {
    const { room } = this.core;
    // 默认对本地音频开启回声消除，屏幕共享音频不开启回声消除
    const { sdkAppId, userId, userSig, assetsPath = this.core.assetsPath, audioReference, processLevel, enableDump, isLocalAudioNeedAudioProcess = true, isScreenAudioNeedAudioProcess = false } = options;
    this.core.room.audioManager.isLocalAudioNeedAudioProcess = isLocalAudioNeedAudioProcess;
    this.core.room.audioManager.isScreenAudioNeedAudioProcess = isScreenAudioNeedAudioProcess;

    if (!assetsPath) throw new RtcError({
      code: ErrorCode.INVALID_PARAMETER,
      message: 'you need to deploy the assets of the npm package and set assetsPath param in TRTC.create()'
    });
    this.core.validateSourceNode();
    await this.preload(`${assetsPath}/audioProcessor-wasm.js`);
    if (isLocalAudioNeedAudioProcess && !this.localAudioWorkletNode) {
      const { sign, status, timestamp } = await this.getAuthData(sdkAppId, userId, userSig);
      this.localAudioWorkletNode = new AudioWorkletNode(this.audioContext, 'trtc-audio-processor', {
        numberOfInputs: 2, // inputs[0]: capture, inputs[1]: playback
        numberOfOutputs: 1
      });
      this.initWorkletNode(this.localAudioWorkletNode, 'localAudio', sdkAppId, userId, timestamp, sign, status, room);
    }
    if (isScreenAudioNeedAudioProcess && !this.screenAudioWorkletNode) {
      const { sign, status, timestamp } = await this.getAuthData(sdkAppId, userId, userSig);
      this.screenAudioWorkletNode = new AudioWorkletNode(this.audioContext, 'trtc-audio-processor', {
        numberOfInputs: 2, // inputs[0]: capture, inputs[1]: playback
        numberOfOutputs: 1
      });
      this.initWorkletNode(this.screenAudioWorkletNode, 'screenAudio', sdkAppId, userId, timestamp, sign, status, room);
    }
    if (!this.mixNode) {
      this.mixNode = this.audioContext.createGain();
      this.mixNode.gain.value = 1;
    }
    if (!this.silentNode) {
      this.silentNode = this.audioContext.createConstantSource();
      this.silentNode.offset.setValueAtTime(0, this.audioContext.currentTime);
      this.silentNode.start();
    }
    this.localAudioWorkletNode?.port.postMessage({ type: 'enable' });
    this.screenAudioWorkletNode?.port.postMessage({ type: 'enable' });
    room.audioManager.addAudioProcessor(this.mixNode, this.silentNode, this.localAudioWorkletNode, this.screenAudioWorkletNode);
    // 混合额外的音频参考信号
    if (!isUndefined(audioReference)) {
      audioReference.forEach(source => {
        this.customAudioReferenceMap.set(source, `${AUDIO_REFERENCE_TYPE.OTHER}-${this.audioRefId++}`);
        this.core.room.audioManager.updateAudioReference({ type: AudioReferenceChangeType.ADD, audioReference: source, refId: `${AUDIO_REFERENCE_TYPE.OTHER}-${this.audioRefId++}` });
      });
    }
    if (!isUndefined(processLevel)) {
      this.localAudioWorkletNode?.port.postMessage({ type: 'setConfig',  data: { aecEnable: 1, aecNlpLevel: processLevel } });
    }
    if (!isUndefined(enableDump)) {
      this.localAudioWorkletNode?.port.postMessage({ type: 'dump', data: { enable: enableDump } });
    }
  }

  async update(options: UpdateAudioProcessorOptions) {
    const { audioReference, enableDump, processLevel } = options;
    if (!isUndefined(audioReference)) {
      // remove ref
      this.customAudioReferenceMap.forEach((audioRefId, source) => {
        this.customAudioReferenceMap.delete(source);
        this.core.room.audioManager.updateAudioReference({ type: AudioReferenceChangeType.REMOVE, refId: audioRefId });
      });
      // add ref
      audioReference.forEach(source => {
        this.customAudioReferenceMap.set(source, `${AUDIO_REFERENCE_TYPE.OTHER}-${this.audioRefId++}`);
        this.core.room.audioManager.updateAudioReference({ type: AudioReferenceChangeType.ADD, audioReference: source, refId: `${AUDIO_REFERENCE_TYPE.OTHER}-${this.audioRefId++}` });
      });
    }
    if (!isUndefined(processLevel)) {
      this.localAudioWorkletNode?.port.postMessage({
        type: 'setConfig',
        data: {
          aecEnable: 1,
          aecNlpLevel: processLevel
        }
      });
    }
    if (!isUndefined(enableDump)) {
      this.localAudioWorkletNode?.port.postMessage({
        type: 'dump',
        data: {
          enable: enableDump
        }
      });
      this.screenAudioWorkletNode?.port.postMessage({
        type: 'dump',
        data: {
          enable: enableDump
        }
      });
    }
  }

  async stop() {
    const { room } = this.core;
    this.localAudioWorkletNode?.port.postMessage({ type: 'disable' });
    this.screenAudioWorkletNode?.port.postMessage({ type: 'disable' });
    await room.audioManager.removeAudioProcessor(this.localAudioWorkletNode, this.screenAudioWorkletNode);
  }

  destroy(): void {
    if (this.localAudioWorkletNode) {
      this.localAudioWorkletNode.port.onmessage = null;
    }
    if (this.screenAudioWorkletNode) {
      this.screenAudioWorkletNode.port.onmessage = null;
    }
    this.uninstallEvent();
  }

  async getAuthData(sdkAppId: number, userId: string, userSig: string) {
    const timestamp = String(Date.now()).slice(0, -3);
    const { auth, sign, status, message } = await httpAuth({ sdkAppId, userSig, userId, timestamp });
    // 校验请求
    if (!auth) {
      this.log.info(`audioProcessor: ${userId} auth result: ${auth}. Message: ${message}`);
      throw new RtcError({
        code: ErrorCode.INVALID_PARAMETER,
        message
      });
    }
    return { sign, status, timestamp };
  }

  initWorkletNode(node: AudioWorkletNode, nodeType: 'localAudio' | 'screenAudio', sdkAppId: number, userId: string, timestamp: string, sign: string, status: string, room: IRoom) {
    node.port.postMessage({
      type: 'init',
      data: {
        sdkAppId: String(sdkAppId),
        userId,
        timestamp,
        sign,
        status
      }
    });
    node.port.onmessage = (e: MessageEvent) => {
      const { data } = e;
      switch (data.type) {
        case 'cost':
          // 打印每 30s 统计的处理耗时数据. data.hist = [a, b, c, d] 表示 30s 內处理耗时落在 0-5ms, 5-10ms, 10-20ms, 超过20ms的各区间百分比
          const logLevel = data?.value > 10 ? 'info' : 'debug';
          this.log[logLevel](`${nodeType === 'localAudio' ? '' : `[${nodeType}] `}avg cost: ${data.value} max: ${data?.max}(${formatDate(new Date(data?.maxCostTimestamp))}) hist: ${data?.hist?.join(' ')}`);
          return;
        case 'log':
          this.log[data.logLevel as 'debug' | 'info' | 'warn' | 'error'](`${nodeType === 'localAudio' ? '' : `[${nodeType}] `}${data.value}`);
          return;
        case 'dump':
          innerEmitter.emit(INNER_EVENT.AUDIO_PROCESSOR_DEBUG, { room, data: data.value, type: nodeType === 'localAudio' ? 'dump' : 'dump-screen-audio' });
          return;
        case 'detectEcho':
          this.log.warn(`${nodeType === 'localAudio' ? '' : `[${nodeType}] `}detect echo: ${isMobile() ? getDeviceModel() : getOSString()}`);
          return;
      }
    };
  }

  async handleLocalAudioStarted(data: { room: IRoom }) {
    if (!this.hitTest(data.room) || this.core.room.scheduleResult.config?.audioProcessor !== true) return;
    try {
      await this.core.trtc.startPlugin('AudioProcessor', {
        sdkAppId: this.core.room.sdkAppId,
        userId: this.core.room.userId,
        userSig: this.core.room.userSig
      });
      this.log.warn('audio processor auto start success');
    } catch (error) {
      this.log.warn(`audio processor auto start failed, error: ${error}`);
    }
  }

  async handleLocalAudioStopped(data: { room: IRoom }) {
    if (!this.hitTest(data.room) || this.core.room.scheduleResult.config?.audioProcessor !== true) return;
    await this.core.trtc.stopPlugin('AudioProcessor');
  }

  installEvent() {
    this.core.innerEmitter.on(INNER_EVENT.LOCAL_TRACK_PUBLISHED, this.handleLocalAudioStarted, this);
    this.core.innerEmitter.on(INNER_EVENT.LOCAL_AUDIO_STOPPED, this.handleLocalAudioStopped, this);
  }

  uninstallEvent() {
    this.core.innerEmitter.off(INNER_EVENT.LOCAL_TRACK_PUBLISHED, this.handleLocalAudioStarted, this);
    this.core.innerEmitter.off(INNER_EVENT.LOCAL_AUDIO_STOPPED, this.handleLocalAudioStopped, this);
  }

  hitTest(room: IRoom) {
    return room === this.core.room;
  }
}

function getAudioProcessorConfigUrl(sdkAppId: number | string, type: 'main' | 'backup' = NAME.MAIN) {
  return `https://${scheduleProxy[type] || getScheduleDomain(sdkAppId, type)}/api/v1/audioAiAuth`;
}

async function httpAuth({
  sdkAppId,
  userId,
  userSig,
  timestamp
}: {
  sdkAppId: number,
  userId: string,
  userSig: string,
  timestamp: string;
}) {
  const configDomain = getAudioProcessorConfigUrl(sdkAppId);
  const url = `${configDomain}?sdkAppId=${sdkAppId}&userId=${userId}&userSig=${userSig}&timestamp=${timestamp}`;
  const res = await fetch(url);
  const { data: { errCode, errMsg, sign, status } } = await res.json();
  // code: 0, data: { errMsg, sign, status: '1'}, msg: 'Ok'
  if (status === '1') {
    return {
      auth: true,
      sign,
      status,
      message: errMsg
    };
  }
  const docPath = isOverseaSdkAppId(sdkAppId) ? 'https://trtc.io/document/42734?platform=web&product=rtcengine&menulabel=coresdk' : 'https://cloud.tencent.com/document/product/647/44247';
  const failed = 'Init RTCAudioProcessor failed.';
  let reason = '';
  switch (errCode) {
    case 1:
      reason = 'Please check your params.';
      break;
    case 2:
      reason = `You need to buy packages. Refer to: ${docPath}`;
      break;
    case 3:
      reason = 'Server is invalid. Please contact our engineer. ';
      break;
    case 4:
      reason = `Your packages is not active. Refer to: ${docPath}`;
      break;
    case 5:
      reason = `Your packages is expired. Refer to: ${docPath}`;
      break;
    case 6:
      reason = 'Your version is not supported.';
      break;
  }
  return {
    auth: false,
    status,
    message: errMsg ? `${failed} Reason: ${errMsg}. ${reason}` : `${failed}, ${reason}`
  };
}
